import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, ArrowLeft } from 'lucide-react';
import { API } from '@/App';

const PageEditorPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState({
    page_title: '',
    content_blocks: [],
    meta_description: '',
    published: false
  });

  useEffect(() => {
    if (slug && slug !== 'new') {
      fetchPage();
    } else {
      setLoading(false);
    }
  }, [slug]);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la page');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (slug && slug !== 'new') {
        await axios.put(`${API}/pages/${slug}`, page, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page mise à jour avec succès');
      } else {
        const newSlug = page.page_title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
        await axios.post(`${API}/pages?slug=${newSlug}`, page, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Page créée avec succès');
        navigate(`/admin/pages/${newSlug}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const addBlock = (type) => {
    const newBlock = {
      id: Date.now().toString(),
      type,
      content: getDefaultContent(type)
    };
    setPage({
      ...page,
      content_blocks: [...page.content_blocks, newBlock]
    });
  };

  const getDefaultContent = (type) => {
    switch (type) {
      case 'hero':
        return {
          title: 'Titre principal',
          subtitle: 'Sous-titre ou description',
          backgroundImage: ''
        };
      case 'text':
        return {
          content: 'Votre contenu texte ici...'
        };
      case 'image':
        return {
          src: '',
          alt: '',
          caption: ''
        };
      case 'cards':
        return {
          title: 'Section avec cartes',
          cards: [
            { title: 'Carte 1', description: 'Description', icon: '' },
            { title: 'Carte 2', description: 'Description', icon: '' },
            { title: 'Carte 3', description: 'Description', icon: '' }
          ]
        };
      case 'list':
        return {
          title: 'Liste',
          items: ['Item 1', 'Item 2', 'Item 3']
        };
      default:
        return {};
    }
  };

  const updateBlock = (blockId, newContent) => {
    setPage({
      ...page,
      content_blocks: page.content_blocks.map(block =>
        block.id === blockId ? { ...block, content: newContent } : block
      )
    });
  };

  const deleteBlock = (blockId) => {
    setPage({
      ...page,
      content_blocks: page.content_blocks.filter(block => block.id !== blockId)
    });
  };

  const moveBlock = (blockId, direction) => {
    const index = page.content_blocks.findIndex(b => b.id === blockId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === page.content_blocks.length - 1)
    ) {
      return;
    }

    const newBlocks = [...page.content_blocks];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
    
    setPage({ ...page, content_blocks: newBlocks });
  };

  const renderBlockEditor = (block, index) => {
    switch (block.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <Label>Titre principal</Label>
              <Input
                value={block.content.title || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, title: e.target.value })}
                placeholder="Titre"
              />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Textarea
                value={block.content.subtitle || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, subtitle: e.target.value })}
                placeholder="Sous-titre ou description"
                rows={3}
              />
            </div>
          </div>
        );

      case 'text':
        return (
          <div>
            <Label>Contenu</Label>
            <Textarea
              value={block.content.content || ''}
              onChange={(e) => updateBlock(block.id, { ...block.content, content: e.target.value })}
              placeholder="Votre contenu ici..."
              rows={6}
            />
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <Label>URL de l'image</Label>
              <Input
                value={block.content.src || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, src: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Texte alternatif</Label>
              <Input
                value={block.content.alt || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, alt: e.target.value })}
                placeholder="Description de l'image"
              />
            </div>
            <div>
              <Label>Légende (optionnel)</Label>
              <Input
                value={block.content.caption || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, caption: e.target.value })}
                placeholder="Légende"
              />
            </div>
          </div>
        );

      case 'list':
        return (
          <div className="space-y-4">
            <div>
              <Label>Titre de la liste</Label>
              <Input
                value={block.content.title || ''}
                onChange={(e) => updateBlock(block.id, { ...block.content, title: e.target.value })}
                placeholder="Titre"
              />
            </div>
            <div>
              <Label>Items (un par ligne)</Label>
              <Textarea
                value={(block.content.items || []).join('\n')}
                onChange={(e) => updateBlock(block.id, { ...block.content, items: e.target.value.split('\n').filter(i => i.trim()) })}
                placeholder="Item 1&#10;Item 2&#10;Item 3"
                rows={5}
              />
            </div>
          </div>
        );

      default:
        return <p className="text-gray-500 text-sm">Type de bloc non supporté</p>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-4 lg:p-8">
          <div className="animate-pulse">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/pages')}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour aux pages
            </Button>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 tracking-tight">
              {slug && slug !== 'new' ? 'Modifier la page' : 'Nouvelle page'}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={page.published ? 'success' : 'secondary'}>
              {page.published ? 'Publiée' : 'Brouillon'}
            </Badge>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Page Settings */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Paramètres de la page</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre de la page</Label>
                <Input
                  value={page.page_title}
                  onChange={(e) => setPage({ ...page, page_title: e.target.value })}
                  placeholder="Ex: À propos"
                />
              </div>

              <div>
                <Label>Description META (SEO)</Label>
                <Textarea
                  value={page.meta_description || ''}
                  onChange={(e) => setPage({ ...page, meta_description: e.target.value })}
                  placeholder="Description pour les moteurs de recherche"
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Statut</Label>
                <Select
                  value={page.published ? 'published' : 'draft'}
                  onValueChange={(val) => setPage({ ...page, published: val === 'published' })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="published">Publié</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t">
                <Label className="mb-3 block">Ajouter un bloc</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" variant="outline" onClick={() => addBlock('hero')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Hero
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addBlock('text')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Texte
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addBlock('image')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Image
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => addBlock('list')}>
                    <Plus className="w-3 h-3 mr-1" />
                    Liste
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Content Blocks */}
          <div className="lg:col-span-2 space-y-4">
            {page.content_blocks.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500 mb-4">Aucun bloc de contenu</p>
                  <p className="text-sm text-gray-400">Ajoutez des blocs depuis le panneau latéral</p>
                </CardContent>
              </Card>
            ) : (
              page.content_blocks.map((block, index) => (
                <Card key={block.id}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{block.type}</Badge>
                        <span className="text-sm text-gray-500">Bloc {index + 1}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveBlock(block.id, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => moveBlock(block.id, 'down')}
                          disabled={index === page.content_blocks.length - 1}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteBlock(block.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {renderBlockEditor(block, index)}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PageEditorPage;
